import md5 from "md5";
import { isAnaglyphOrientation } from "./anaglyph_orientation";

export const CALIBRATION_PIXELS_PER_CM = 28;
export const MIN_GUESSED_SCREEN_HEIGHT_CM = 7;
export const GUESSED_DISTANCE_MULTIPLIER = 3;
export const MIN_GUESSED_DISTANCE_CM = 30;
export const MIN_CALIBRATION_BRIGHTNESS = 0.2;
export const MAX_CALIBRATION_BRIGHTNESS = 1;
export const DEFAULT_CALIBRATION_BRIGHTNESS = 0.75;

/**
 * Screen-dimension tolerance (CSS px) used when deciding whether a stored
 * calibration still matches the current device. Matches the threshold
 * already proven in production by the v1 exercise runtime.
 */
export const CALIBRATION_DEVICE_MATCH_TOLERANCE_PX = 50;

/** Height of a standard ID-1 card (credit/debit/ID), in centimeters. */
export const CREDIT_CARD_HEIGHT_CM = 5.398;

/** Diameter of a US quarter, in centimeters. */
export const US_QUARTER_HEIGHT_CM = 2.426;

/**
 * Normalize devicePixelRatio to a stable precision to avoid floating-point jitter.
 * Rounds to 2 decimal places to tolerate tiny differences like 1.25 vs 1.2500000000000002.
 */
export function normalizeDevicePixelRatio(pixel_ratio: number): number {
  return Math.round(pixel_ratio * 100) / 100;
}

export interface CalibrationScreen {
  width: number;
  height: number;
  colorDepth: number;
}

export interface CalibrationDeviceIdentity {
  width_px: number;
  height_px: number;
  pixel_ratio: number;
  fingerprint: string;
}

export interface CalibrationGuess extends CalibrationDeviceIdentity {
  height_cm: number;
  distance_cm: number;
}

export interface StoredColourCorrection {
  red_brightness: number;
  blue_brightness: number;
  green_brightness: number;
}

export interface StoredCalibration {
  fingerprint: string;
  width_px: number;
  height_px: number;
  pixel_ratio: number;
  height_cm: number;
  distance_cm: number;
  anaglyph_orientation: string;
  colour_correction: StoredColourCorrection;
}

export function normalizedCalibrationScreenDimensions(
  current_screen: Pick<CalibrationScreen, "width" | "height"> = screen,
): Pick<CalibrationDeviceIdentity, "width_px" | "height_px"> {
  return {
    width_px: Math.max(current_screen.width, current_screen.height),
    height_px: Math.min(current_screen.width, current_screen.height),
  };
}

export function guessedCalibrationHeightCm(
  height_px: number,
  pixel_ratio: number,
): number {
  return Math.max(
    MIN_GUESSED_SCREEN_HEIGHT_CM,
    Math.round(height_px / (CALIBRATION_PIXELS_PER_CM * pixel_ratio)),
  );
}

export function guessedCalibrationDistanceCm(height_cm: number): number {
  return Math.max(
    MIN_GUESSED_DISTANCE_CM,
    height_cm * GUESSED_DISTANCE_MULTIPLIER,
  );
}

export interface CalibrationReferenceImage {
  /** Physical size of the real object the user holds against the screen (cm). */
  item_cm: number;
  /**
   * The screen's short edge in *live* CSS pixels — CSS pixels as they are
   * currently being laid out, which is what `style.height` is interpreted in.
   *
   * In fullscreen this is `Math.min(window.innerWidth, window.innerHeight)`.
   * `screen.width`/`screen.height` are not a substitute: they are live CSS
   * pixels in Firefox but frozen at their 100%-zoom values in Chrome and
   * Safari, so using them makes the result wrong by the zoom factor there.
   */
  screen_short_edge_css_px: number;
  /** The screen's short edge in centimeters, as the user currently estimates it. */
  screen_cm: number;
  /** `window.visualViewport.scale`; 1 when unavailable or not pinched. */
  pinch_scale?: number;
}

/**
 * CSS-pixel height at which to render the calibration wizard's reference image
 * so that it appears at the true physical size of the object it depicts.
 *
 * The result is invariant under browser zoom, pinch-zoom and devicePixelRatio.
 * Rendering `css_height` pixels puts `css_height × screen_cm /
 * screen_short_edge_css_px × pinch_scale` centimeters on the glass, which
 * reduces to `item_cm` whenever `screen_cm` is the screen's true physical
 * size — the point at which the user stops adjusting the slider. Browser zoom
 * cancels because `screen_short_edge_css_px` is a live measurement; pinch
 * cancels through the divisor, which is needed because pinch magnifies
 * optically without altering CSS geometry.
 */
export function calibrationReferenceImageHeightPx({
  item_cm,
  screen_short_edge_css_px,
  screen_cm,
  pinch_scale = 1,
}: CalibrationReferenceImage): number {
  return (item_cm * screen_short_edge_css_px) / (screen_cm * pinch_scale);
}

/**
 * How closely a viewport's two per-axis scale factors against the screen must
 * agree before the viewport may be treated as a whole-screen measurement, as a
 * fraction of the larger factor.
 *
 * Deliberately tight. Failing this check costs nothing — the caller keeps the
 * last measurement it trusted — whereas accepting a viewport that is off by
 * this much makes the resulting calibration off by the same proportion.
 */
export const VIEWPORT_FILLS_SCREEN_TOLERANCE = 0.02;

export interface ViewportScreenDimensions {
  screen_width: number;
  screen_height: number;
  viewport_width: number;
  viewport_height: number;
}

/**
 * Whether a viewport covers the whole screen.
 *
 * Used to decide whether a fullscreen document's `innerWidth`/`innerHeight` may
 * be read as the screen's own dimensions. Being in fullscreen is not on its own
 * sufficient: an on-screen keyboard shrinks the *layout* viewport on Android
 * Chrome and Firefox, whose default `interactive-widget=resizes-content`
 * behaviour resizes layout rather than only the visual viewport, so a focused
 * text field can halve `innerHeight` without leaving fullscreen.
 *
 * Decided by comparing the viewport to the screen one axis at a time and
 * requiring the two scale factors to agree, rather than by comparing dimensions
 * outright. A scale factor absorbs every browser's zoom model — `screen.*` is
 * frozen at its 100%-zoom values in Chrome and Safari and live in Firefox, but
 * both axes always move together — while still catching an occlusion, which by
 * its nature eats one axis and leaves the other alone.
 *
 * The axes are paired directly rather than normalized to short/long edges: a
 * viewport occluded on one axis can share the screen's short/long ratio by
 * coincidence (an 800x1280 screen showing 800x500 has the screen's own 0.625
 * ratio inverted), so normalizing would let exactly the case this guards
 * against slip through. Direct pairing assumes `screen.width`/`screen.height`
 * follow the device's orientation, which holds everywhere this runs: the only
 * platform known to misreport them in landscape is older iOS, where the
 * Fullscreen API is unavailable for arbitrary elements and so this check is
 * never reached.
 */
export function viewportFillsScreen({
  screen_width,
  screen_height,
  viewport_width,
  viewport_height,
}: ViewportScreenDimensions): boolean {
  if (
    !(screen_width > 0) ||
    !(screen_height > 0) ||
    !(viewport_width > 0) ||
    !(viewport_height > 0)
  ) {
    return false;
  }

  const width_scale = viewport_width / screen_width;
  const height_scale = viewport_height / screen_height;

  return (
    Math.abs(width_scale - height_scale) <=
    Math.max(width_scale, height_scale) * VIEWPORT_FILLS_SCREEN_TOLERANCE
  );
}

export function calibrationFingerprint(
  current_screen: CalibrationScreen = screen,
  pixel_ratio: number = window.devicePixelRatio,
): string {
  const normalized_pixel_ratio = normalizeDevicePixelRatio(pixel_ratio);
  return md5(
    `${current_screen.colorDepth}|${current_screen.width}|${current_screen.height}|${normalized_pixel_ratio}`,
  );
}

export function currentCalibrationDeviceIdentity(
  current_screen: CalibrationScreen = screen,
  pixel_ratio: number = window.devicePixelRatio,
): CalibrationDeviceIdentity {
  const normalized_pixel_ratio = normalizeDevicePixelRatio(pixel_ratio);
  return {
    ...normalizedCalibrationScreenDimensions(current_screen),
    pixel_ratio: normalized_pixel_ratio,
    fingerprint: calibrationFingerprint(current_screen, pixel_ratio),
  };
}

/**
 * Ratio of the current devicePixelRatio to the devicePixelRatio recorded at
 * calibration time. A value away from 1 means the browser's zoom level has
 * drifted since calibration (desktop page zoom moves devicePixelRatio in
 * both Chrome and Firefox; it is unaffected by mobile pinch-zoom).
 */
export function effectiveZoomFactor(
  calibration_pixel_ratio: number,
  current_pixel_ratio: number = window.devicePixelRatio,
): number {
  return (
    normalizeDevicePixelRatio(current_pixel_ratio) /
    normalizeDevicePixelRatio(calibration_pixel_ratio)
  );
}

function withinDeviceMatchTolerance(a: number, b: number): boolean {
  return Math.abs(a - b) <= CALIBRATION_DEVICE_MATCH_TOLERANCE_PX;
}

type DeviceMatchIdentity = Pick<
  CalibrationDeviceIdentity,
  "width_px" | "height_px" | "pixel_ratio"
>;

/**
 * Whether a stored calibration's screen dimensions still correspond to the
 * current device, tolerating browser zoom drift rather than requiring an
 * exact match.
 *
 * `screen.width`/`screen.height` behave differently across browsers when
 * the page is zoomed: Chrome and Safari keep them constant, while Firefox
 * moves them (in lockstep with devicePixelRatio and window.innerWidth).
 * Rather than pick one browser's model, this accepts either: a match on the
 * raw current dimensions (the Chrome/Safari case), or a match once the
 * current dimensions are scaled by the devicePixelRatio drift since
 * calibration (the Firefox case).
 *
 * A genuine device change to a different resolution fails both checks and
 * is still caught. An external monitor that happens to share the current
 * screen's resolution and devicePixelRatio is not distinguishable here:
 * `screen.colorDepth` is hardcoded to 24 by every major browser, so it
 * cannot serve as a monitor-swap signal, and the calibrated `height_cm` is
 * user-entered and can't be re-verified automatically.
 */
export function calibrationStillMatchesDevice(
  calibration: DeviceMatchIdentity,
  current_identity: DeviceMatchIdentity = currentCalibrationDeviceIdentity(),
): boolean {
  const zoom = effectiveZoomFactor(calibration.pixel_ratio, current_identity.pixel_ratio);

  const raw_match =
    withinDeviceMatchTolerance(current_identity.width_px, calibration.width_px) &&
    withinDeviceMatchTolerance(current_identity.height_px, calibration.height_px);
  const zoom_adjusted_match =
    withinDeviceMatchTolerance(current_identity.width_px * zoom, calibration.width_px) &&
    withinDeviceMatchTolerance(current_identity.height_px * zoom, calibration.height_px);

  return raw_match || zoom_adjusted_match;
}

export function guessCalibrationMeasurements(
  current_screen: CalibrationScreen = screen,
  pixel_ratio: number = window.devicePixelRatio,
): CalibrationGuess {
  const identity = currentCalibrationDeviceIdentity(current_screen, pixel_ratio);
  const height_cm = guessedCalibrationHeightCm(identity.height_px, pixel_ratio);
  return {
    ...identity,
    height_cm,
    distance_cm: guessedCalibrationDistanceCm(height_cm),
  };
}

function isPositiveFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}

/**
 * A single channel's brightness constrained to the range storage accepts.
 *
 * `isCompleteStoredCalibration()` treats an out-of-range brightness as a
 * malformed record and rejects the calibration whole, so a value that would
 * fall outside the range is brought back into it rather than allowed to reach
 * storage and silently cost the user their whole calibration. A value that is
 * not a number at all carries no intent to preserve, so it falls back to the
 * default.
 */
export function clampCalibrationBrightness(brightness: unknown): number {
  if (typeof brightness !== "number" || !Number.isFinite(brightness)) {
    return DEFAULT_CALIBRATION_BRIGHTNESS;
  }
  return Math.min(
    MAX_CALIBRATION_BRIGHTNESS,
    Math.max(MIN_CALIBRATION_BRIGHTNESS, brightness),
  );
}

/** The colour correction a screen starts with, before the user calibrates it. */
export function defaultColourCorrection(): StoredColourCorrection {
  return {
    red_brightness: DEFAULT_CALIBRATION_BRIGHTNESS,
    blue_brightness: DEFAULT_CALIBRATION_BRIGHTNESS,
    green_brightness: DEFAULT_CALIBRATION_BRIGHTNESS,
  };
}

/** A colour correction with every channel brought into the supported range. */
export function clampedColourCorrection(
  colour_correction: Partial<StoredColourCorrection> | null | undefined,
): StoredColourCorrection {
  return {
    red_brightness: clampCalibrationBrightness(colour_correction?.red_brightness),
    blue_brightness: clampCalibrationBrightness(colour_correction?.blue_brightness),
    green_brightness: clampCalibrationBrightness(colour_correction?.green_brightness),
  };
}

function isValidCalibrationBrightness(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isFinite(value) &&
    value >= MIN_CALIBRATION_BRIGHTNESS &&
    value <= MAX_CALIBRATION_BRIGHTNESS
  );
}

export function isCompleteStoredCalibration(
  calibration: unknown,
): calibration is StoredCalibration {
  if (!calibration || typeof calibration !== "object") {
    return false;
  }

  const candidate = calibration as Partial<StoredCalibration>;
  if (
    !isPositiveFiniteNumber(candidate.width_px) ||
    !isPositiveFiniteNumber(candidate.height_px) ||
    !isPositiveFiniteNumber(candidate.pixel_ratio) ||
    !isPositiveFiniteNumber(candidate.height_cm) ||
    !isPositiveFiniteNumber(candidate.distance_cm) ||
    !isAnaglyphOrientation(candidate.anaglyph_orientation) ||
    typeof candidate.fingerprint !== "string"
  ) {
    return false;
  }

  const colour_correction = candidate.colour_correction;
  return Boolean(
    colour_correction &&
    typeof colour_correction === "object" &&
    isValidCalibrationBrightness(colour_correction.red_brightness) &&
    isValidCalibrationBrightness(colour_correction.blue_brightness) &&
    isValidCalibrationBrightness(colour_correction.green_brightness),
  );
}

/**
 * Whether a stored calibration is still usable on the current device.
 *
 * Checks the cheap exact-fingerprint match first (covers the common case of
 * an unchanged device at an unchanged zoom level). If that fails,
 * falls back to a tolerant comparison of the underlying screen dimensions
 * and devicePixelRatio so that browser zoom drift alone doesn't force a
 * recalibration — see `calibrationStillMatchesDevice()`.
 */
export function calibrationMatchesCurrentDevice<T extends StoredCalibration>(
  calibration: T | null | undefined,
  current_identity: CalibrationDeviceIdentity = currentCalibrationDeviceIdentity(),
): calibration is T {
  return Boolean(
    isCompleteStoredCalibration(calibration) &&
    (calibration.fingerprint === current_identity.fingerprint ||
      calibrationStillMatchesDevice(calibration, current_identity)),
  );
}

export function parseStoredCalibration<T extends StoredCalibration>(
  serialized_calibration: string | null | undefined,
  current_identity: CalibrationDeviceIdentity = currentCalibrationDeviceIdentity(),
): T | null {
  if (!serialized_calibration) {
    return null;
  }

  try {
    const calibration = JSON.parse(serialized_calibration) as T;
    return calibrationMatchesCurrentDevice(calibration, current_identity)
      ? calibration
      : null;
  } catch {
    return null;
  }
}

export function resolveStoredCalibration<T extends StoredCalibration>(
  server_calibrations: readonly T[] = [],
  serialized_local_calibration: string | null | undefined = null,
  current_identity: CalibrationDeviceIdentity = currentCalibrationDeviceIdentity(),
): T | null {
  const server_calibration = server_calibrations.find((calibration) =>
    calibrationMatchesCurrentDevice(calibration, current_identity)
  );
  if (server_calibration) {
    return server_calibration;
  }

  return parseStoredCalibration<T>(
    serialized_local_calibration,
    current_identity,
  );
}
